import * as React from 'react'
import { useRouter } from 'next/router'
import { GetStaticPaths, GetStaticProps } from 'next'
import apolloClient from '@/lib/utils/apollo-client'
import getCraftSiteHandle from '@/lib/utils/get-craft-site-handle'
import Seo from '@/components/Seo'
import AppHeader from '@/components/AppHeader'
import AppFooter from '@/components/AppFooter'
import dynamic from 'next/dynamic'
import getPreviewToken from '@/lib/utils/get-preview-token'
import ArticleSignOff from '@/components/ArticleSignOff'

import { gql } from '@apollo/client'
import { QUERY_SITE_WIDE } from '@/gql/shared-queries'
import { FRAGMENT_IMAGE } from '@/gql/fragments'

import { SiteWideQuery_globalSet } from '@/gql/types/SiteWideQuery'
import { StandardArticleQuery_entry_articles_article_Entry } from '@/gql/types/StandardArticleQuery'
import { FragmentImage } from '@/gql/types/FragmentImage'

type StandardPageType = {
    entry: StandardArticleQuery_entry_articles_article_Entry
    siteWide: SiteWideQuery_globalSet
}

const componentList: { [key: string]: React.ComponentType<any> } = {
    articleTitle: dynamic(() => import(`@/components/Blocks/ArticleModules/Title`)),
    heroImage: dynamic(() => import(`@/components/Blocks/ArticleModules/HeroImage`)),
    image: dynamic(() => import(`@/components/Blocks/ArticleModules/ImageBlock`)),
    copy: dynamic(() => import(`@/components/Blocks/Modules/Copy`)),
    video: dynamic(() => import(`@/components/Blocks/ArticleModules/Video`)),
}

const StandardPage: React.FunctionComponent<StandardPageType> = ({ entry, siteWide }) => {
    const router = useRouter()
    const metaImage = entry.metaImage![0] as FragmentImage

    const components = entry.articleModules!.map((module: any) => {
        return { name: componentList[module?.typeHandle!], data: module }
    })

    return (
        <>
            <Seo
                data={entry}
                siteWide={siteWide}
                title={entry.metaTitle!}
                description={entry.metaDescription!}
                image={metaImage}
                canonical={`${router.asPath}`}
            />
            <AppHeader mode="fixed" siteWide={siteWide} />
            {components.map((Component, index) => {
                return (
                    <Component.name key={index} data={Component.data} postDate={entry.postDate} />
                )
            })}
            <ArticleSignOff />
            <AppFooter siteWide={siteWide} bgColor="bg-black" />
        </>
    )
}

export const getStaticProps: GetStaticProps = async ({ params, locale, preview, previewData }) => {
    const previewToken = getPreviewToken(previewData, preview)

    const { data } = await apolloClient(previewToken).query({
        query: gql`
            ${FRAGMENT_IMAGE}
            query StandardArticleQuery($site: [String], $slug: [String]) {
                entry(section: "articles", site: $site, slug: $slug) {
                    title
                    slug
                    postDate
                    ... on articles_article_Entry {
                        metaTitle
                        metaDescription
                        metaImage {
                            ...FragmentImage
                        }
                        summary
                        articleModules {
                            ... on articleModules_articleTitle_BlockType {
                                typeHandle
                                heading
                            }
                            ... on articleModules_copy_BlockType {
                                typeHandle
                                description
                            }
                            ... on articleModules_heroImage_BlockType {
                                typeHandle
                                image {
                                    ...FragmentImage
                                }
                            }
                            ... on articleModules_image_BlockType {
                                typeHandle
                                image {
                                    ...FragmentImage
                                }
                            }
                            ... on articleModules_video_BlockType {
                                typeHandle
                                videoUrl
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            site: getCraftSiteHandle(locale),
            slug: params!.slug,
        },
    })

    const { data: siteWide } = await apolloClient(previewToken).query({
        query: QUERY_SITE_WIDE,
    })

    if (data.entry === null) {
        return {
            notFound: true,
            revalidate: 1,
        }
    }

    return {
        props: {
            entry: data.entry as StandardArticleQuery_entry_articles_article_Entry,
            siteWide: siteWide.globalSet as SiteWideQuery_globalSet,
        },
        revalidate: 1,
    }
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking',
    }
}

export default StandardPage
